import { Skeleton } from '@/components/ui/skeleton';

/**
 * RunLoadingSkeleton — the agent pages' loading state while a run /
 * analysis / product detail fetch is in flight. Mirrors the shape of what
 * replaces it (the AgentRunCard meta strip, then a result panel) so the
 * page doesn't flash from a bare one-line "Loading run…" into a full
 * layout — content appears where the bones already were.
 *
 * Pure pulse animation, no text by design: these fetches resolve in well
 * under a second, and a skeleton reads as "arriving" where a gray italic
 * sentence read as "broken". Screen readers get a real status via
 * aria-busy + the sr-only label.
 */
export function RunLoadingSkeleton({ label = 'Loading run' }: { label?: string }) {
  return (
    <div aria-busy="true" role="status">
      <span className="sr-only">{label}</span>

      {/* AgentRunCard-shaped meta strip: Run # / Status / Product / Date */}
      <div className="flex items-center gap-7 flex-wrap py-[18px] px-6 mb-[22px] bg-white border border-line rounded-2xl shadow-[0_2px_10px_rgba(20,40,70,0.04)]">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-6 w-24" />
          </div>
        ))}
        <div className="ml-auto flex flex-col gap-2 items-end">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-6 w-28" />
        </div>
      </div>

      {/* Result-panel block */}
      <div className="bg-white border border-line rounded-2xl shadow-card p-6">
        <Skeleton className="h-5 w-56 mb-5" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-[92%]" />
          <Skeleton className="h-4 w-[96%]" />
          <Skeleton className="h-4 w-[60%]" />
        </div>
        <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20 hidden sm:block" />
        </div>
      </div>
    </div>
  );
}
